import {
  useInfoPanelDetails,
  useRemoveMembersFromChannelMutation,
} from 'gcs-common/clients/api/entities/channels/channel-api';
import {
  BlockItem,
  MailItem,
  PhoneItem,
  RemoveMemberItem,
  ReportItem,
} from 'gcs-common/components/InfoPanel/ListComponents/ListItem';
import PanelItemsSection from 'gcs-common/components/InfoPanel/ListComponents/PanelItemsSection';
import MemberHeader from 'gcs-common/components/InfoPanel/MemberHeader/MemberHeader';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import MemberPermissions from 'gcs-common/components/MemberPermissions/MemberPermissions';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { ButtonVariant } from 'gcs-common/design-system/ui/button';
import { ConfirmationDialog } from 'gcs-common/design-system/ui/confirmation-dialog';
import { CHANNEL_TYPE } from 'gcs-common/helper/channelTypeHelper';
import FEATURE_FLAGS from 'gcs-common/helper/featureFlagsHelper';
import USER_TYPE from 'gcs-common/helper/userTypeHelper';
import { UserType } from 'gcs-common/slices/administration/administrationUsers/administrationUsersConstants';
import { getCurrentUserId, getHasFeatureFlag, getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getIsMobile } from 'gcs-common/slices/init/initSelectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

interface MemberDetailsProps {
  selectedUserId: string;
  onReportUserClicked?: VoidFunction;
  onBlockUserClicked?: VoidFunction;
  onBack: VoidFunction;
}

const MemberDetails = (props: MemberDetailsProps) => {
  const { selectedUserId, onReportUserClicked, onBlockUserClicked, onBack } = props;
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();
  const currentUserIsAgent = useSelector(getIsAgent);
  const isMultipleCraftsmenEnabled = useSelector(
    getHasFeatureFlag(FEATURE_FLAGS.AGENTS_CHAT_CREATION_WITH_MULTIPLE_CRAFTSMEN),
  );
  const isMobile = useSelector(getIsMobile);
  const currentUserId = useSelector(getCurrentUserId);
  const { mutateAsync, isPending } = useRemoveMembersFromChannelMutation();
  const isOwnProfile = currentUserId === selectedUserId;
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const { data: infoPanelData, isLoading } = useInfoPanelDetails(selectedChannelId);

  const isLastCraftsman =
    !!infoPanelData && infoPanelData.users.filter((u) => u.userType === UserType.CRAFTSMAN).length === 1;

  const selectedUser =
    infoPanelData?.users?.find((u) => u.id === selectedUserId) ||
    infoPanelData?.assignedTeamUsers?.find((u) => u.id === selectedUserId) ||
    infoPanelData?.assignedAgentUsers?.find((u) => u.id === selectedUserId);

  if (isLoading || !infoPanelData || !selectedUser) {
    return <LoadingIndicator />;
  }

  const selectedUserIsAgent = selectedUser.userType === USER_TYPE.AGENT;
  const primaryHouseId = infoPanelData.primaryHouseId || '';

  const removeMember = () => {
    mutateAsync({
      path: {
        channel_id: selectedChannelId,
      },
      body: {
        user_id: selectedUserId,
      },
    }).then(() => {
      toast({
        description: 'Nutzer erfolgreich entfernt',
        duration: 5000,
        variant: 'success',
      });
      onBack();
    });
  };

  return (
    <div className="w-full">
      <ConfirmationDialog
        title={`Willst du wirklich ${selectedUser.friendlyName || 'Unbekannt'} entfernen?`}
        confirmLabel="Entfernen"
        confirmText="Der Nutzer kann den Chat danach nicht mehr einsehen."
        closeLabel="Abbrechen"
        close={() => setShowRemoveConfirm(false)}
        confirm={removeMember}
        isLoading={isPending}
        confirmButtonVariant={ButtonVariant.solid_error}
        showModal={showRemoveConfirm}
      />
      <MemberHeader
        selectedUserId={selectedUserId}
        name={selectedUser.friendlyName}
        image={selectedUser.imageUrl}
        selectedUserIsAgent={selectedUserIsAgent}
        userStatus={selectedUser.status}
      />
      <div className="px-4">
        <PanelItemsSection className="space-y-2">
          {currentUserIsAgent &&
            !selectedUserIsAgent &&
            isMultipleCraftsmenEnabled &&
            infoPanelData.channelType === CHANNEL_TYPE.SALES_SPACE && (
              <div>
                <RemoveMemberItem
                  disabled={isLastCraftsman}
                  onClick={() => setShowRemoveConfirm(true)}
                  isLoading={isPending}
                />
              </div>
            )}
          {selectedUser.email && (
            <div>
              <MailItem email={selectedUser.email} />
            </div>
          )}

          {selectedUser.phoneNumber && (
            <div>
              <PhoneItem phoneNumber={selectedUser.phoneNumber} />
            </div>
          )}
        </PanelItemsSection>
        {!selectedUserIsAgent && currentUserIsAgent && (
          <MemberPermissions houseId={primaryHouseId} userId={selectedUserId} />
        )}
        {isMobile && !isOwnProfile && (
          <PanelItemsSection title="Aktionen" className="pt-4">
            {onReportUserClicked && (
              <div>
                <ReportItem onClick={onReportUserClicked} />
              </div>
            )}
            {onBlockUserClicked && (
              <div>
                <BlockItem onClick={onBlockUserClicked} />
              </div>
            )}
          </PanelItemsSection>
        )}
      </div>
    </div>
  );
};

export default MemberDetails;
